import { ReleaseResourceAllocationType } from '~/types';
import { useReleaseResourceAllocation } from '~/modules/resourcing/common/hooks';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { RESOURCE_USERS_QUERY } from './useResourceUsers';
import { RESOURCE_ALLOCATIONS_QUERY_BY_USER } from './useResourceAllocationsSummary';
import useQuickAllocationContext from './useQuickAllocationContext';
import {
  tryLoadCachedResourceUsersQuery,
  tryLoadCachedAllocationSummaryQuery,
  getUpdatedResourceUsersSummaryAllocation,
  getUpdatedResourceAllocationSummary
} from './useFulfilledResourceAllocationHandler';
import { mapProjectStatusToProjectWorkflowStages } from './utils';

export const updateDeleteCache = ({
  resourceUsersCurrentPage,
  userSort,
  allocationSort,
  dateRange,
  periodResolution,
  previousResourceAllocation,
  me,
  scale,
  filter,
  projectStatusFilterOption
}) => ({ releaseType, releaseDate }) => (proxy, mutationResponse) => {
  const {
    data: {
      releaseResourceAllocation: { resourceAllocation }
    }
  } = mutationResponse;

  const variables = {
    page: resourceUsersCurrentPage,
    pagesize: 50,
    dateRange,
    sort: userSort,
    periodResolution,
    filter,
    isAvailabilityEnabled: true,
    isViewResourceAllocation: false,
    isViewResourceActualsEnabled: false,
    skipMerge: true
  };

  const results = tryLoadCachedResourceUsersQuery({
    proxy,
    variables
  });

  if (!results) return;

  const { resourceUsers2 } = results;

  const updatedResourceUsers = getUpdatedResourceUsersSummaryAllocation({
    resourceUsers: resourceUsers2,
    resourceAllocation: previousResourceAllocation,
    updateHoursCallback: ({ periodHours, summary }) =>
      periodHours > 0
        ? Math.round(summary.allocatedDuration.hours * 100) / 100 - periodHours
        : summary.allocatedDuration.hours
  });

  const startDateInFuture =
    mapIsoStringtoUtcObject(resourceAllocation.startDate) >
    mapIsoStringtoUtcObject(releaseDate);

  proxy.writeQuery({
    query: RESOURCE_USERS_QUERY,
    variables,
    data: {
      resourceUsers2:
        releaseType === ReleaseResourceAllocationType.Asof && !startDateInFuture
          ? getUpdatedResourceUsersSummaryAllocation({
              resourceUsers: updatedResourceUsers,
              resourceAllocation,
              updateHoursCallback: ({ periodHours, summary }) =>
                periodHours > 0
                  ? summary.allocatedDuration.hours + periodHours
                  : summary.allocatedDuration.hours
            })
          : updatedResourceUsers
    }
  });

  const allocationVariables = {
    page: 1,
    dateRange,
    userUri: previousResourceAllocation.user.userUri,
    periodResolution,
    limit: 5,
    sort: allocationSort,
    projectWorkflowStages: mapProjectStatusToProjectWorkflowStages(
      projectStatusFilterOption
    )
  };

  const result = tryLoadCachedAllocationSummaryQuery({
    proxy,
    variables: allocationVariables
  });

  if (!result) return;

  const { resourceAllocationsSummary } = result;

  const newResourceAllocationsSummary = {
    ...resourceAllocationsSummary,
    resourceAllocationSummaryByDateRange: resourceAllocationsSummary.resourceAllocationSummaryByDateRange.filter(
      alloc => alloc.resourceAllocation.id !== previousResourceAllocation.id
    )
  };

  proxy.writeQuery({
    query: RESOURCE_ALLOCATIONS_QUERY_BY_USER,
    variables: allocationVariables,
    data: {
      resourceAllocationsSummary:
        releaseType === ReleaseResourceAllocationType.Asof && !startDateInFuture
          ? getUpdatedResourceAllocationSummary({
              me,
              scale,
              resourceAllocation,
              resourceAllocationsSummary: newResourceAllocationsSummary
            })
          : newResourceAllocationsSummary
    }
  });
};

export const useReleaseResourceAllocationHandler = ({
  resourceAllocation,
  allocationSort,
  me,
  scale
}) => {
  const {
    resourceUsersCurrentPage,
    sort: userSort,
    dateRange,
    periodResolution,
    filter,
    projectStatusFilterOption
  } = useQuickAllocationContext();
  const {
    releaseResourceAllocation,
    data,
    loading
  } = useReleaseResourceAllocation({
    resourceAllocation,
    updateDeleteCache: updateDeleteCache({
      resourceUsersCurrentPage,
      userSort: [userSort],
      allocationSort,
      dateRange,
      periodResolution,
      previousResourceAllocation: resourceAllocation,
      me,
      scale,
      filter,
      projectStatusFilterOption
    })
  });

  return {
    releaseResourceAllocation,
    data,
    loading
  };
};

export default useReleaseResourceAllocationHandler;
